import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import NavigationDots from 'components/commercetools-ui/atoms/navigation-dots/NavigationDots';
import NavigationPlayPauseButton from 'components/commercetools-ui/atoms/navigation-dots/NavigationPlayPauseButton';
import { useNavigationDots } from 'components/commercetools-ui/atoms/navigation-dots/useNavigationDots';
import Slider from 'components/commercetools-ui/atoms/slider';
import 'components/commercetools-ui/atoms/navigation-dots/styles.css';
import { trackViewPromotion, trackSelectPromotion } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile } from 'helpers/utils/screensizes';
import BannerTile from './banner-tile';
import { HeroBannerProps } from './types';
import clsx from 'clsx';
import { setFFIDsForPagination } from '../curated-slider/helpers';
import HeroContentBlock from './components/hero-content-block';

const HeroBanner: React.FC<HeroBannerProps> = ({
  banner,
  autoRotationInterval,
  isAutoRotation,
  aspectRatioDesktop,
  aspectRatioMobile,
}) => {
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(isAutoRotation);
  const [swiperInstance, setSwiperInstance] = React.useState<any>(null);
  const [isDesktopSize] = useMediaQuery(mobile);
  const [slideDescription, setSlideDescription] = useState<string>('');

  const {
    currentSlide,
    isVisible: dotsVisible,
    handleDotClick,
  } = useNavigationDots({
    swiper: swiperInstance,
    totalSlides: banner?.length || 0,
    autoHide: false,
  });

  // Handle autoplay pause/resume
  React.useEffect(() => {
    if (swiperInstance?.autoplay) {
      if (isAutoPlaying) {
        swiperInstance.autoplay.start();
      } else {
        swiperInstance.autoplay.stop();
      }
    }
  }, [isAutoPlaying, swiperInstance]);
  useEffect(() => {
    const prevButtons = document.querySelectorAll('.swiper-button-prev, .slider-prev, [data-ffid*="prev"]');
    const nextButtons = document.querySelectorAll('.swiper-button-next, .slider-next, [data-ffid*="next"]');

    // 2. Safely apply the missing discernible text labels
    prevButtons.forEach((btn) => {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Previous slide');
      }
    });

    nextButtons.forEach((btn) => {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Next slide');
      }
    });
  }, []);

  // GA4: view_promotion — fires when a banner slide becomes visible
  const viewedSlides = useRef(new Set<number>());
  useEffect(() => {
    if (!banner?.length) return;

    const tile = banner[currentSlide];
    const headline = tile?.contentBlock?.[0]?.headline?.[0]?.headlineLabel;
    const subtext = tile?.contentBlock?.[0]?.subtext?.[0]?.subtextLabel;

    setSlideDescription(headline || subtext);

    if (viewedSlides.current.has(currentSlide)) return;

    trackViewPromotion({
      creativeName: tile?.bgImageAlt || undefined,
      creativeSlot: `pos_${currentSlide + 1}`,
      promotionId: `hero_banner_${currentSlide}`,
      promotionName: headline || `Hero Banner ${currentSlide + 1}`,
    });

    viewedSlides.current.add(currentSlide);
  }, [currentSlide, banner]);

  const currentBlocks =
    Array.isArray(banner) && banner[currentSlide]?.contentBlock ? banner[currentSlide].contentBlock : [];

  const currentLegalItems = currentBlocks
    .flatMap((block) => block?.legalText || [])
    .filter((l) => !!l && (l.showText ? l.legalLabel?.trim() : l.subhtml?.trim() || l.markdown?.trim()));

  const handleToggleAutoplay = React.useCallback(() => {
    if (!swiperInstance) {
      setIsAutoPlaying((p) => !p);
      return;
    }

    if (!isAutoPlaying) {
      if (typeof swiperInstance.slideNext === 'function') {
        swiperInstance.slideNext(0);
      }

      setIsAutoPlaying(true);
      requestAnimationFrame(() => {
        if (swiperInstance.autoplay) {
          swiperInstance.autoplay.stop();
          swiperInstance.autoplay.start();
        }
      });
    } else {
      // If currently playing and user pauses
      setIsAutoPlaying(false);
      swiperInstance.autoplay?.stop?.();
    }
  }, [swiperInstance, isAutoPlaying]);

  const [isSliderFocused, setIsSliderFocused] = useState<boolean>(false);

  const handleSliderContainerFocus = useCallback(() => {
    setIsSliderFocused(true);
  }, []);

  const handleSliderContainerBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null;
    const container = event.currentTarget;

    if (nextFocusedElement) {
      if (container.contains(nextFocusedElement)) {
        return;
      }

      setIsSliderFocused(false);
      return;
    }

    // Defer blur handling because autoplay slide transitions can momentarily blur
    // before focus is restored within the carousel. Capture `container` synchronously
    // above since React nulls out the pooled event's currentTarget before this runs.
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (!container || (activeElement && container.contains(activeElement))) {
        return;
      }

      setIsSliderFocused(false);
    });
  }, []);

  return (
    <div className="mx-0 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <section
        id="hero-banner-container"
        className="hero-banner container"
        aria-label="Hero banner carousel"
        aria-roledescription="carousel"
        data-ffid="fra-hero-banner"
        onFocus={handleSliderContainerFocus}
        onBlur={handleSliderContainerBlur}
      >
        <div id="hero-banner-wrapper" className="z-4 relative px-20 sm:px-0 md:pt-20 mb-10">
          <div id="hero-banner-slider-wrapper" aria-live="polite" aria-atomic="false" className="relative">
            <Slider
              className="rounded-xl sm:rounded-2xl"
              arrows={isDesktopSize}
              allowTouchMove={true}
              autoHeight={true}
              simulateTouch={true}
              touchStartPreventDefault={false}
              touchMoveStopPropagation={false}
              spaceBetween={20}
              uniqueFFIDText={'hero-banner'}
              slidesPerView={1}
              allowArrowsOnTouchDevice={true}
              navPosition="right"
              speed={300}
              onInit={(swiper) => {
                setSwiperInstance(swiper);
                setFFIDsForPagination(swiper, 'hero-banner');
              }}
              onSwiper={(swiper) => {
                setSwiperInstance(swiper);
              }}
              autoplay={
                isAutoPlaying
                  ? {
                      delay: Math.max(0, autoRotationInterval ?? 0) * 1000,
                      disableOnInteraction: false,
                    }
                  : false
              }
              a11y={{
                enabled: true,
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
                firstSlideMessage: 'This is the first slide',
                lastSlideMessage: 'This is the last slide',
                paginationBulletMessage: 'Go to slide {{index}}',
                containerRoleDescriptionMessage: '',
                slideRole: 'presentation',
                itemRoleDescriptionMessage: '',
              }}
              slideDescription={isSliderFocused ? slideDescription : undefined}
            >
              {banner?.map((tile, index) => {
                const headlineText =
                  tile?.contentBlock?.[0]?.headline?.[0]?.headlineLabel || `Hero Banner ${index + 1}`;
                return (
                  <Fragment key={`banner_${tile?.bgImageAlt ?? ''}${index}`}>
                    <div
                      id={`hero-banner-tile-wrapper-${index}`}
                      role="tabpanel"
                      aria-roledescription="slide"
                      aria-label={`${index + 1} of ${banner.length}`}
                      className='relative'
                    >
                      <BannerTile
                        keyInfo={index}
                        backgroundImage={{
                          bgImageSrc: isDesktopSize ? tile.bgImageSrc : tile?.bgImageSrcMobile,
                          bgImageAlt: isDesktopSize ? tile.bgImageAlt : tile?.bgImageAltMobile,
                        }}
                        contentBlock={tile.contentBlock}
                        aspectRatioDesktop={aspectRatioDesktop}
                        aspectRatioMobile={aspectRatioMobile}
                        onCtaClick={() => {
                          trackSelectPromotion({
                            creativeName: tile?.bgImageAlt || undefined,
                            creativeSlot: `pos_${index + 1}`,
                            promotionId: `hero_banner_${index}`,
                            promotionName: headlineText,
                          });
                        }}
                      />
                      {banner?.length > 1 && index === currentSlide && (
                        <div
                          className={clsx(
                            'hero-banner-control-play-pause',
                            currentLegalItems && !currentLegalItems[0]?.showText && '',
                          )}
                          id="navigation-play-pause-button-wrapper"
                          data-ffid="ctr-play-pause-button"
                        >
                          <NavigationPlayPauseButton isPlaying={isAutoPlaying} onToggle={handleToggleAutoplay} contentBlock={tile.contentBlock} />
                        </div>
                      )}
                    </div>
                    {(!isDesktopSize && index === currentSlide && <div className="mb-8 sm:hidden">
                      <HeroContentBlock
                        contentBlock={tile?.contentBlock}
                        isDesktopSize={false}
                        keyInfo={currentSlide}
                        onCtaClick={() => {
                          const headline = tile?.contentBlock?.[0]?.headline?.[0]?.headlineLabel;
                          trackSelectPromotion({
                            creativeName: tile?.bgImageAlt || undefined,
                            creativeSlot: `pos_${currentSlide + 1}`,
                            promotionId: `hero_banner_${currentSlide}`,
                            promotionName: headline || `Hero Banner ${currentSlide + 1}`,
                          });
                        }}
                      />
                    </div>)}
                  </Fragment>
                );
              })}
            </Slider>
          </div>

          {/* Hero banner carousel controls */}
          {banner && banner.length > 1 && (
            <div id="hero-banner-controls" aria-label="Carousel controls" data-ffid="ctr-hero-banner-controls">
              <div className="mt-20 flex justify-center" id="navigation-dots-wrapper" data-ffid="ctr-carousel-dots">
                <NavigationDots
                  maxVisibleDots={4}
                  swiper={swiperInstance}
                  currentSlide={currentSlide}
                  totalSlides={banner.length}
                  onDotClick={handleDotClick}
                  className={`${dotsVisible ? 'navigation-dots--visible' : 'navigation-dots--hidden'}`}
                  isAutoPlaying={isAutoPlaying}
                  autoplayDelayMs={Math.max(0, autoRotationInterval ?? 0) * 1000}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HeroBanner;
